import { useCallback, useEffect, useRef, useState } from 'react'
import { Cart, useCartProps } from '@haus-storefront-react/cart'
import { cartChannel, useEventBusOn } from '@haus-storefront-react/core'
import type { OrderLine } from '@haus-storefront-react/shared-types'
import { useTranslation } from 'react-i18next'
import {
  AddToCartButton,
  Link,
  Price,
  Typography,
  WroomImage,
  useWidgetReadyAfterInitialLoad,
  WidgetReadyBoundary,
} from '@components'
import { TrashIcon } from '@components/icons'
import type { CartAttributes } from './_propsFn'
import styles from './Cart.module.scss'
import { useActiveOrder, useOrderStates } from '@haus-storefront-react/hooks'
import { useFallbackImage } from '../../hooks/useFallbackImage'

interface CartWidgetContentProps extends CartAttributes {
  order: unknown
  error: unknown
  isLoading: boolean
  shouldBlur: boolean
}

const CartWidgetContent = ({
  order,
  error,
  isLoading,
  showImage,
  showPrice,
  showAddToCartButton,
  showRemoveButton,
  showQuantityText,
  configStyles,
  shouldBlur,
}: CartWidgetContentProps) => {
  const { t } = useTranslation()
  const { data: activeOrder } = useActiveOrder()
  const { transitionOrderToState } = useOrderStates()
  const buttonVariant = configStyles?.addToCartButtonVariant?.variant ?? 'secondary'
  const productNameTypography = configStyles?.productNameTypography
  const priceTypography = configStyles?.priceTypography
  const listPriceTypography = configStyles?.listPriceTypography
  const quantityTypography = configStyles?.quantityTypography
  const fallbackImage = useFallbackImage()

  const hasError = Boolean(error)
  const hasOrder = Boolean(order)
  const hasSettledContent = hasOrder || hasError || !isLoading
  const isReady = useWidgetReadyAfterInitialLoad({
    hasSettledContent,
    hasVisibleContent: hasSettledContent,
    isLoading,
  })

  const ensureAddingItemsState = useCallback(async () => {
    if (activeOrder?.state === 'ArrangingPayment') {
      await transitionOrderToState('AddingItems')
    }
  }, [activeOrder?.state, transitionOrderToState])

  return (
    <WidgetReadyBoundary isReady={isReady}>
      <div className={styles.orderLines}>
        {hasError && (
          <div className={styles.error} role="alert">
            <Typography variant="body">{t('cart.error')}</Typography>
          </div>
        )}
        {hasOrder && (
          <Cart.Items.Root
            callbacks={{
              preRemove: ensureAddingItemsState,
            }}
          >
            {({ orderLines }) => (
              <ul className={styles.list}>
                {orderLines?.map((line: OrderLine) => {
                  const variant = line.productVariant
                  const imageSrc = line.featuredAsset?.preview
                  return (
                    <li key={line.id} className={styles.listItem}>
                      <Cart.Items.Item orderLine={line}>
                        <div className={styles.itemWrapper}>
                          {showImage && (
                            <div className={styles.imageWrapper}>
                              <WroomImage
                                src={imageSrc ?? fallbackImage}
                                ratio="1:1"
                                alt={variant.name}
                              />
                            </div>
                          )}
                          <div className={styles.itemContent}>
                            <div className={styles.infoWrapper}>
                              <Link
                                href={`/product/${variant.product.slug}`}
                                aria-label={t('a11y.cart.aria.productLink', {
                                  productName: variant.name,
                                })}
                                className={styles.productName}
                              >
                                <Typography
                                  variant={productNameTypography?.variant ?? 'body'}
                                  as={productNameTypography?.tag ?? 'span'}
                                  color={productNameTypography?.color ?? 'body'}
                                >
                                  {variant.name}
                                </Typography>
                              </Link>
                              {showQuantityText && (
                                <Typography
                                  variant={quantityTypography?.variant ?? 'body'}
                                  as={quantityTypography?.tag ?? 'span'}
                                  color={quantityTypography?.color ?? 'body'}
                                  className={styles.quantityText}
                                >
                                  {line.quantity}{' '}
                                  {line.quantity === 1 ? t('cart.piece') : t('cart.pieces')}
                                </Typography>
                              )}
                              <div className={styles.quantity}>
                                {showAddToCartButton && (
                                  <div className={styles.addToCartButtonWrapper}>
                                    <AddToCartButton
                                      key={variant.id}
                                      productVariantId={variant.id}
                                      productVariant={variant}
                                      buttonVariant={buttonVariant}
                                      productName={variant.name}
                                    />
                                  </div>
                                )}
                                {showRemoveButton && (
                                  <Cart.Items.Remove
                                    aria-label={t('a11y.cart.aria.removeItem')}
                                    className={styles.removeButton}
                                  >
                                    <TrashIcon width={16} height={16} />
                                  </Cart.Items.Remove>
                                )}
                              </div>
                            </div>

                            {showPrice && (
                              <div className={styles.orderLinePriceWrapper} aria-busy={shouldBlur}>
                                <Cart.Items.Price>
                                  {({ price, priceWithTax, currencyCode }) => (
                                    <span className={shouldBlur ? styles.priceLoading : undefined}>
                                      <Price
                                        price={price}
                                        priceWithTax={priceWithTax}
                                        listPrice={variant.listPrice}
                                        listPriceWithTax={variant.listPriceWithTax}
                                        currencyCode={currencyCode}
                                        productVariant={variant}
                                        themeConfig={{
                                          priceTypography: {
                                            variant: priceTypography?.variant ?? 'body',
                                            tag: priceTypography?.tag ?? 'span',
                                            color: priceTypography?.color ?? 'primary',
                                          },
                                          listPriceTypography: {
                                            variant: listPriceTypography?.variant ?? 'body',
                                            tag: listPriceTypography?.tag ?? 'span',
                                            color: listPriceTypography?.color ?? 'body',
                                          },
                                        }}
                                        className={styles.pricewrapper}
                                      />
                                    </span>
                                  )}
                                </Cart.Items.Price>
                              </div>
                            )}
                          </div>
                        </div>
                      </Cart.Items.Item>
                    </li>
                  )
                })}
              </ul>
            )}
          </Cart.Items.Root>
        )}
      </div>
    </WidgetReadyBoundary>
  )
}

const CartWidget = ({
  showImage,
  showPrice,
  showAddToCartButton,
  showRemoveButton,
  showQuantityText,
  configStyles,
}: CartAttributes) => {
  const [pricesUpdating, setPricesUpdating] = useState(false)
  const { isFetching } = useCartProps()
  const prevFetchingRef = useRef(isFetching)

  useEventBusOn(cartChannel, 'cart:updating', () => setPricesUpdating(true), undefined, false)
  useEventBusOn(cartChannel, 'cart:error', () => setPricesUpdating(false), undefined, false)

  useEffect(() => {
    if (prevFetchingRef.current && !isFetching && pricesUpdating) {
      setPricesUpdating(false)
    }
    prevFetchingRef.current = isFetching
  }, [isFetching, pricesUpdating])

  return (
    <Cart.Root>
      {({ data: order, error, isLoading, isAdjusting, isRemoving }) => (
        <CartWidgetContent
          order={order}
          error={error}
          isLoading={isLoading}
          showImage={showImage}
          showPrice={showPrice}
          showAddToCartButton={showAddToCartButton}
          showRemoveButton={showRemoveButton}
          showQuantityText={showQuantityText}
          configStyles={configStyles}
          shouldBlur={pricesUpdating || isAdjusting || isRemoving}
        />
      )}
    </Cart.Root>
  )
}

export default CartWidget
